/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { DocumentVersionState } from '../sharedcomponents/algorithm/types';
import { RootState } from '../../app/store';
import pearlApi, { setAuthToken } from '../sharedcomponents/api/pearlApi';
import { PearlUser } from '../sharedcomponents/Login/userSlice';
import { TextInputThunkArgs } from '../sharedcomponents/chat/chatSlice';
import { fetchPageComments } from './network';
import { snackNotifications } from '../sharedcomponents/notification/SnackBarUtils';

export type CommentType = {
  id: string;
  createdAt: string;
  pageId: string;
  content: string;
  state: 'OPEN' | 'RESOLVED';
  user: {
    id: string;
    name: string;
  };
  needs_attention?: boolean; // Whether the comment needs attention by a creator to be resolved. AI sets this to true.
  from_ai?: boolean; // Whether created by open AI.
  documentVersion: {
    id: string;
    createdAt: string;
    state: DocumentVersionState;
  };
};

export interface CommentsState {
  documentVersionId?: string;
  reviewId?: string;
  comments: CommentType[];
  newComment: {
    text: string;
    processing: boolean; // If we've hit submit but not got the response back from the server yet
  };
  currentSearchInReview?: TextInputThunkArgs | undefined;
  fetchedComments: { [key: string]: CommentType[] };
}

const initialState: CommentsState = {
  comments: [],
  newComment: { text: '', processing: false },
  fetchedComments: {},
};

export const addReviewCommentsThunk = createAsyncThunk<
  void,
  { reviewId: string; documentVersionId: string; pageId: string; content: string },
  { state: RootState }
>('commentsSlice/addReviewCommentsThunk', async ({ reviewId, documentVersionId, pageId, content }, thunkAPI) => {
  const res = await pearlApi.post(
    `/documentVersions/${documentVersionId}/consultant-reviews/${reviewId}/pages/${pageId}/comments`,
    {
      data: { content },
    }
  );
  if (res.status === 404 || res.data?.errors) {
    return thunkAPI.rejectWithValue(res.data.errors);
  }
});

export const fetchPageCommentsThunk = createAsyncThunk<
  { pageId: string; comments: CommentType[] },
  string,
  { state: RootState }
>('comments/fetchDocumentVersionComments', async (pageId: string, thunkAPI) => {
  const response = await fetchPageComments(pageId);
  return response;
});

export const fetchReviewTokenInfo = createAsyncThunk<
  void,
  { reviewId: string; documentVersionId: string },
  { state: RootState }
>('commentsSlice/fetchReviewTokenInfo', async ({ reviewId, documentVersionId }, thunkApi) => {
  const userInfo = (await pearlApi.post(`/auth/consultant-reviews/${documentVersionId}/${reviewId}`)).data
    .data as PearlUser;
  setAuthToken(userInfo.accessToken);
});

export const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    newCommentSetText: (state, action) => {
      state.newComment.text = action.payload;
    },
    setReviewInfoFromLink: (state, action) => {
      state.documentVersionId = action.payload.documentVersionId;
      state.reviewId = action.payload.reviewId;
    },
    setCurrentSearchInReview: (state, action) => {
      console.log('setting current review document to search to ', action.payload);
      state.currentSearchInReview = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(addReviewCommentsThunk.fulfilled, (state) => {
        state.newComment = { text: '', processing: false };
        snackNotifications.success('Comment added');
      })
      .addCase(addReviewCommentsThunk.rejected, (state) => {
        snackNotifications.error('Failed to add comment');
      })
      .addCase(fetchPageCommentsThunk.fulfilled, (state, data) => {
        const { pageId, comments } = data.payload;

        const sortedComments = comments?.sort((a, b) => b.createdAt.localeCompare(a.createdAt)) || [];
        state.fetchedComments[pageId] = sortedComments;
      })
      .addCase(fetchPageCommentsThunk.rejected, (state) => {
        snackNotifications.error('Failed to fetch comments for the page');
      });
  },
});
export const { newCommentSetText, setReviewInfoFromLink, setCurrentSearchInReview } = commentsSlice.actions;
export const selectNewComment = (state: RootState) => state.comments.newComment;
export const selectReviewId = (state: RootState) => state.comments.reviewId;
const emptyList: CommentType[] = [];

export const selectComments = (state: RootState, pageId: string) => state.comments.fetchedComments[pageId] || emptyList;
export default commentsSlice.reducer;
